import { localizeDateNames } from "@/lib/dateNames";
import i18n from "@/i18n";
/**
 * EmployeeWorkforceMobile — Prompt 15B §3-§12.
 *
 * Public, standalone mobile surface for the Employee Mobile flow.
 * Renders OUTSIDE the MiseOne manager Layout / Workforce sidebar
 * (§3): no sidebar, no Team, no Review Hours, no Inventory.
 *
 * Screens (§4 · §5 · §9 · §10 · §12):
 *   1. ACTIVATE  — activation code + 6-digit PIN + confirm PIN.
 *      The link the manager copies embeds `c` (company_id),
 *      `e` (employee_id), `a` (activation_code) as URL params
 *      so the employee never sees or types raw UUIDs (§5 · §22).
 *   2. LOGIN     — PIN only, after prior activation on this device.
 *   3. HOME      — name, clock status, Clock In / Clock Out primary,
 *                  own published schedule, Log out.
 *
 * Uses the dedicated `employeeApi` client (§6) so the manager
 * cookie session is never sent. No GPS / photo / biometric (§11).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { applyLanguage } from "@/i18n";
import { MobileLanguageSelector, readEmployeeLanguage }
    from "@/components/workforce/mobile/MobileLanguageSelector";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    employeeApi,
    getDeviceSession,
    getEmployeeIdentity,
    getEmployeeToken,
    setDeviceSession,
    setEmployeeIdentity,
    setEmployeeToken,
} from "@/lib/employeeWorkforceApi";
import {
    clockOutBounds, fmtHM, localYMD, pickValidFinish, scheduledEndConfirmable,
    declaredStartOptions, scheduledStartConfirmable,
} from "@/lib/employeeClockPicker";
import { ClockInStartDialog } from "@/components/workforce/mobile/ClockInStartDialog";
import { LiveMobileClock }
    from "@/components/workforce/mobile/LiveMobileClock";
import { EmployeeBottomNav }
    from "@/components/workforce/mobile/EmployeeBottomNav";
import { formatDurationMinutes, formatScheduleDateHeader }
    from "@/components/workforce/workforceHelpers";

const _PIN_RE = /^\d{6}$/;

function _getUrlParams() {
    if (typeof window === "undefined") return {};
    const p = new URLSearchParams(window.location.search);
    return {
        // Prompt 15B-GATE §1 — the activation link carries ONLY
        // non-secret device-scoping identity. The activation code
        // is NEVER accepted from the URL; the employee types it
        // manually from paper / chat / SMS so it cannot leak to
        // browser history / Referer / server access logs.
        company_id: p.get("c") || "",
        employee_id: p.get("e") || "",
    };
}

function _scrubUrlParams() {
    // Prompt 15B-GATE §1 — remove `c=` / `e=` from the visible
    // address bar immediately after read so identifiers are not
    // shoulder-surfed or persisted in browser history.
    if (typeof window === "undefined") return;
    try {
        if (window.location.search) {
            window.history.replaceState(
                {}, "", window.location.pathname);
        }
    } catch { /* ignore */ }
}

export const _MSG_CLOCK_AMBIGUOUS =
    "We cannot tell which shift you are starting. Ask your manager to record your hours.";
export const _MSG_CLOCK_STALE_SESSION =
    "You are still clocked in from an earlier shift. Clock out now and tell your manager your real finish time.";

function _friendlyClockError(status, code) {
    if (status === 401) return i18n.t("wf.employeeWorkforceMobile.sessionExpiredPleaseLogInAgain", "Session expired. Please log in again.");
    if (code === "WORKFORCE_CLOCK_RESTAURANT_AMBIGUOUS") return i18n.t("wf.EmployeeWorkforceMobile._MSG_CLOCK_AMBIGUOUS", _MSG_CLOCK_AMBIGUOUS);
    if (status === 403) return i18n.t("wf.employeeWorkforceMobile.youAreNotScheduledAtThis", "You are not scheduled at this location.");
    if (code === "WORKFORCE_CLOCK_ALREADY_ACTIVE") {
        return i18n.t("wf.employeeWorkforceMobile.youAreAlreadyClockedIn", "You are already clocked in.");
    }
    if (code === "WORKFORCE_CLOCK_NOT_ACTIVE") {
        return i18n.t("wf.employeeWorkforceMobile.youAreNotCurrentlyClockedIn", "You are not currently clocked in.");
    }
    return i18n.t("wf.employeeWorkforceMobile.somethingWentWrongPleaseTryAgain", "Something went wrong. Please try again.");
}

// CLOCK C2 — an open session older than 16 h is a missing clock-out;
// it is never closed automatically or given an invented end time.
export function _isStaleSession(occurredAt, now = new Date()) {
    if (!occurredAt) return false;
    return now.getTime() - new Date(occurredAt).getTime() > 16 * 3600 * 1000;
}

// Prompt 18B2-P1 §12 — pure helper: does the employee still have any
// scheduled shift ahead on the local `today`? Looks at the immutable
// Published Snapshot (never mutated by clocks). A shift is "remaining"
// if its work_date is today AND either its end_date is a later day
// (overnight span) or its end_time is in the future.
export function _hasRemainingShiftToday(
    schedule, now = new Date(),
) {
    if (!schedule || !Array.isArray(schedule.publications)) return false;
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const today = `${y}-${m}-${d}`;
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const nowHM = `${hh}:${mm}`;
    for (const pub of schedule.publications) {
        for (const s of (pub.shifts || [])) {
            if (s.work_date !== today) continue;
            if (s.end_date && s.end_date > today) return true;
            if ((s.end_time || "") > nowHM) return true;
        }
    }
    return false;
}


function _humanClockTime(iso) {
    if (typeof iso !== "string" || !iso) return "";
    // Local device wall-clock is fine for the "Clocked in at" hint;
    // the canonical UTC is server-authoritative and never surfaced.
    try {
        const d = new Date(iso);
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        return `${hh}:${mm}`;
    } catch { return ""; }
}

export default function EmployeeWorkforceMobile() {
    useTranslation();
    useEffect(() => {
        const lang = readEmployeeLanguage();
        if (lang) applyLanguage(lang);
    }, []);
    const urlParams = useMemo(_getUrlParams, []);
    const storedIdentity = useMemo(getEmployeeIdentity, []);
    // Prompt 15B-GATE §1 — immediately scrub URL params so `c=&e=`
    // no longer live in the address bar / history / Referer once
    // captured. Runs after the first commit of this component.
    useEffect(_scrubUrlParams, []);
    // Prompt 18B2-P5 §11 · §26 — deterministic boot state machine:
    //   A. valid employee_access exists → open Home.
    //   B. access missing/expired + valid trusted-device session
    //      → attempt silent refresh (see effect below).
    //   C. trusted device known but needs re-auth → PIN.
    //   D. no trusted identity → Activation.
    // The initial `screen` here handles A and D. B / C are resolved
    // by the boot effect that either succeeds silently (→ Home) or
    // falls back to PIN.
    const _initialScreen = () => {
        if (urlParams.company_id && urlParams.employee_id) return "activate";
        if (getEmployeeToken()) return "home";
        // §11.B — presence of a trusted-device session on this
        // device means we do NOT show Activation while the silent
        // refresh is in flight. A neutral "booting" screen keeps
        // the flow deterministic.
        if (getDeviceSession() && storedIdentity) return "booting";
        if (storedIdentity) return "login";
        return "activate";
    };
    const [screen, setScreen] = useState(_initialScreen);

    const [companyId, setCompanyId] = useState(
        urlParams.company_id || storedIdentity?.company_id || "");
    const [employeeId, setEmployeeId] = useState(
        urlParams.employee_id || storedIdentity?.employee_id || "");
    const [activationCode, setActivationCode] = useState("");
    const [pin, setPin] = useState("");
    const [pinConfirm, setPinConfirm] = useState("");
    const [error, setError] = useState("");
    const [pending, setPending] = useState(false);

    // Prompt 18B2-P5 §3 · §11 · §12 — trusted-device silent refresh
    // on boot. Runs exactly once when the initial screen resolved to
    // "booting" (i.e. we have identity + device session but no valid
    // access JWT). Success → Home. Failure → PIN (§8). NEVER falls
    // back to Activation merely because the JWT is missing (§9).
    useEffect(() => {
        if (screen !== "booting") return;
        const session = getDeviceSession();
        const identity = getEmployeeIdentity();
        if (!session || !identity) {
            setScreen(identity ? "login" : "activate");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await employeeApi.post(
                    "/workforce/employee/auth/refresh", {
                        company_id: identity.company_id,
                        employee_id: identity.employee_id,
                        device_session_token: session.device_session_token,
                    });
                if (cancelled) return;
                setEmployeeToken(data.token);
                setDeviceSession({
                    device_session_id: data.device_session_id,
                    device_session_token: data.device_session_token,
                });
                setScreen("home");
            } catch (err) {
                if (cancelled) return;
                // §8 — trusted session invalid → PIN, not Activation.
                setDeviceSession(null);
                setScreen("login");
            }
        })();
        return () => { cancelled = true; };
    // Runs once on the first commit; subsequent screen changes
    // must NOT retrigger a refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _doActivate = useCallback(async () => {
        if (pending) return;
        setError("");
        if (!_PIN_RE.test(pin)) {
            setError(i18n.t("wf.employeeWorkforceMobile.pinMustBeExactly6Digits", "PIN must be exactly 6 digits."));
            return;
        }
        if (pin !== pinConfirm) {
            setError(i18n.t("wf.employeeWorkforceMobile.pinsDoNotMatch", "PINs do not match."));
            return;
        }
        if (!companyId || !employeeId || !activationCode) {
            setError(i18n.t("wf.employeeWorkforceMobile.missingActivationLinkAskYourManager", "Missing activation link. Ask your manager for a new one."));
            return;
        }
        setPending(true);
        try {
            // Prompt 18B2-P5 §14 — successful activation returns the
            // fresh employee_access JWT AND a trusted-device session
            // in ONE round-trip. No follow-up PIN login is needed.
            const { data: actData } = await employeeApi.post(
                "/workforce/employee/activate", {
                    company_id: companyId,
                    employee_id: employeeId,
                    activation_code: activationCode,
                    pin,
                });
            // Persist non-secret identity so future login only needs PIN.
            setEmployeeIdentity({
                company_id: companyId, employee_id: employeeId });
            setEmployeeToken(actData.token);
            setDeviceSession({
                device_session_id: actData.device_session_id,
                device_session_token: actData.device_session_token,
            });
            setActivationCode(""); setPin(""); setPinConfirm("");
            setScreen("home");
        } catch (err) {
            const status = err?.response?.status;
            const code = err?.response?.data?.detail?.code;
            if (status === 422 && code === "WORKFORCE_INVALID_PIN_FORMAT") {
                setError(i18n.t("wf.employeeWorkforceMobile.pinMustBeExactly6Digits", "PIN must be exactly 6 digits."));
            } else if (status === 401) {
                setError(i18n.t("wf.employeeWorkforceMobile.thisActivationLinkIsInvalidOr", "This activation link is invalid or expired."));
            } else {
                setError(i18n.t("wf.employeeWorkforceMobile.couldNotActivatePleaseTryAgain", "Could not activate. Please try again."));
            }
        } finally { setPending(false); }
    }, [pending, pin, pinConfirm, companyId, employeeId, activationCode]);

    const _doLogin = useCallback(async () => {
        if (pending) return;
        setError("");
        if (!_PIN_RE.test(pin)) {
            setError(i18n.t("wf.employeeWorkforceMobile.pinMustBeExactly6Digits", "PIN must be exactly 6 digits."));
            return;
        }
        if (!companyId || !employeeId) {
            setError(i18n.t("wf.employeeWorkforceMobile.thisDeviceHasNoEmployeeUse", "This device has no employee. Use your activation link."));
            return;
        }
        setPending(true);
        try {
            const { data } = await employeeApi.post(
                "/workforce/employee/login",
                { company_id: companyId, employee_id: employeeId, pin });
            setEmployeeToken(data.token);
            // Prompt 18B2-P5 §8 — PIN unlock establishes / renews the
            // trusted-device session so future normal reopens skip
            // the PIN screen.
            setDeviceSession({
                device_session_id: data.device_session_id,
                device_session_token: data.device_session_token,
            });
            setPin("");
            setScreen("home");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) {
                setError(i18n.t("wf.employeeWorkforceMobile.wrongPinPleaseTryAgain", "Wrong PIN. Please try again."));
            } else {
                setError(i18n.t("wf.employeeWorkforceMobile.couldNotSignInPleaseTry", "Could not sign in. Please try again."));
            }
        } finally { setPending(false); }
    }, [pending, pin, companyId, employeeId]);

    const _logout = useCallback(() => {
        // Prompt 18B2-P5A §2 · §3 · §6 — EXPLICIT logout / lock is a
        // security action, NOT an ordinary close. Clear both the
        // short-lived employee_access JWT AND the client's trusted-
        // device credential so no silent refresh is possible until
        // the employee re-authenticates with the PIN. Employee
        // identity (company_id / employee_id) is retained so the
        // next boot lands on the PIN screen — NOT Activation.
        //
        // Ordinary close / minimise / suspend NEVER routes through
        // this helper; the boot state machine handles those.
        setEmployeeToken(null);
        setDeviceSession(null);
        setPin("");
        setError("");
        setScreen(getEmployeeIdentity() ? "login" : "activate");
    }, []);

    const _forgetDevice = useCallback(() => {
        setEmployeeToken(null);
        setEmployeeIdentity(null);
        // Prompt 18B2-P5 §9 · §15 — an explicit "forget/reset this
        // device" clears the trusted-device session so the next open
        // legitimately requires Activation Code.
        setDeviceSession(null);
        setCompanyId(""); setEmployeeId(""); setActivationCode("");
        setPin(""); setPinConfirm("");
        setError("");
        setScreen("activate");
    }, []);

    return (
        <div
            data-testid="employee-workforce-mobile"
            className="min-h-screen bg-[#faf6ef] flex flex-col
                        items-stretch text-neutral-900"
        >
            <main className="flex-1 w-full max-w-md mx-auto
                            pt-[env(safe-area-inset-top)]">
                {screen === "booting" && (
                    <div
                        data-testid="employee-workforce-booting"
                        className="text-sm text-neutral-500 text-center pt-10"
                    >
                        {i18n.t("wf.employeeWorkforceMobile.signingYouIn", "Signing you in…")}
                    </div>
                )}
                {(screen === "activate" || screen === "login") && (
                    <div className="px-6 pt-8">
                        <header className="text-center pb-6">
                            <h1
                                className="text-neutral-900 text-3xl"
                                style={{
                                    fontFamily:
                                        "Georgia, 'Times New Roman', serif",
                                }}
                            >
                                {i18n.t("wf.employeeWorkforceMobile.miseone", "MiseOne")}
                            </h1>
                            <p className="text-neutral-500 text-sm mt-1
                                        tracking-[0.14em] uppercase">
                                {i18n.t("wf.employeeWorkforceMobile.workforce", "Workforce")}
                            </p>
                            <MobileLanguageSelector className="mt-4" />
                        </header>
                    </div>
                )}
                {screen === "activate" && (
                    <ActivateScreen
                        activationCode={activationCode}
                        onActivationCodeChange={setActivationCode}
                        pin={pin}
                        onPinChange={setPin}
                        pinConfirm={pinConfirm}
                        onPinConfirmChange={setPinConfirm}
                        onActivate={_doActivate}
                        pending={pending}
                        error={error}
                    />
                )}
                {screen === "login" && (
                    <LoginScreen
                        pin={pin}
                        onPinChange={setPin}
                        onLogin={_doLogin}
                        onUseActivation={() => setScreen("activate")}
                        pending={pending}
                        error={error}
                    />
                )}
                {screen === "home" && (
                    <HomeScreen
                        onSessionExpired={_logout}
                        onLogout={_logout}
                        onForgetDevice={_forgetDevice}
                    />
                )}
            </main>
        </div>
    );
}


function ActivateScreen({
    activationCode, onActivationCodeChange,
    pin, onPinChange, pinConfirm, onPinConfirmChange,
    onActivate, pending, error,
}) {
    return (
        <div
            data-testid="employee-workforce-activate"
            className="space-y-4"
        >
            <div>
                <h2 className="text-lg font-medium text-neutral-900">
                    {i18n.t("wf.employeeWorkforceMobile.activateEmployeeAccess", "Activate employee access")}
                </h2>
                <p className="text-sm text-neutral-500 mt-1">
                    {i18n.t("wf.employeeWorkforceMobile.enterTheCodeFromYourManager", "Enter the code from your manager and create a 6-digit PIN.")}
                </p>
            </div>
            <div className="space-y-2">
                <Label htmlFor="ew-activation-code" className="text-xs
                                text-neutral-500">
                    {i18n.t("wf.employeeWorkforceMobile.activationCode", "Activation code")}
                </Label>
                <Input
                    id="ew-activation-code"
                    data-testid="employee-workforce-activation-code"
                    value={activationCode}
                    onChange={(e) => onActivationCodeChange(e.target.value)}
                    autoComplete="one-time-code"
                    autoCapitalize="characters"
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="ew-pin" className="text-xs text-neutral-500">
                    Create 6-digit PIN
                </Label>
                <Input
                    id="ew-pin"
                    data-testid="employee-workforce-activate-pin"
                    inputMode="numeric"
                    autoComplete="new-password"
                    maxLength={6}
                    value={pin}
                    onChange={(e) => onPinChange(
                        e.target.value.replace(/[^0-9]/g, ""))}
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="ew-pin-confirm"
                        className="text-xs text-neutral-500">
                    Confirm PIN
                </Label>
                <Input
                    id="ew-pin-confirm"
                    data-testid="employee-workforce-activate-pin-confirm"
                    inputMode="numeric"
                    autoComplete="new-password"
                    maxLength={6}
                    value={pinConfirm}
                    onChange={(e) => onPinConfirmChange(
                        e.target.value.replace(/[^0-9]/g, ""))}
                />
            </div>
            {error && (
                <p
                    data-testid="employee-workforce-activate-error"
                    className="text-sm text-red-600"
                >
                    {error}
                </p>
            )}
            <Button
                data-testid="employee-workforce-activate-submit"
                onClick={onActivate}
                disabled={pending}
                className="w-full"
            >
                {i18n.t("wf.employeeWorkforceMobile.activate", "Activate")}
            </Button>
        </div>
    );
}


function LoginScreen({
    pin, onPinChange, onLogin, onUseActivation, pending, error,
}) {
    return (
        <div
            data-testid="employee-workforce-login"
            className="space-y-4"
        >
            <div>
                <h2 className="text-lg font-medium text-neutral-900">
                    Enter PIN
                </h2>
                <p className="text-sm text-neutral-500 mt-1">
                    {i18n.t("wf.employeeWorkforceMobile.signInWithYour6Digit", "Sign in with your 6-digit PIN.")}
                </p>
            </div>
            <div className="space-y-2">
                <Label htmlFor="ew-login-pin"
                        className="text-xs text-neutral-500">
                    PIN
                </Label>
                <Input
                    id="ew-login-pin"
                    data-testid="employee-workforce-login-pin"
                    inputMode="numeric"
                    autoComplete="current-password"
                    maxLength={6}
                    value={pin}
                    onChange={(e) => onPinChange(
                        e.target.value.replace(/[^0-9]/g, ""))}
                />
            </div>
            {error && (
                <p
                    data-testid="employee-workforce-login-error"
                    className="text-sm text-red-600"
                >
                    {error}
                </p>
            )}
            <Button
                data-testid="employee-workforce-login-submit"
                onClick={onLogin}
                disabled={pending}
                className="w-full"
            >
                {i18n.t("wf.employeeWorkforceMobile.signIn", "Sign in")}
            </Button>
            <button
                type="button"
                data-testid="employee-workforce-use-activation"
                onClick={onUseActivation}
                className="text-sm text-neutral-500 underline w-full text-center"
            >
                {i18n.t("wf.employeeWorkforceMobile.useActivationCode", "Use activation code")}
            </button>
        </div>
    );
}


function HomeScreen({ onSessionExpired, onLogout, onForgetDevice }) {
    const [me, setMe] = useState(null);
    const [clock, setClock] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [scheduleErr, setScheduleErr] = useState("");
    const [clockErr, setClockErr] = useState("");
    const [pending, setPending] = useState(false);
    // Prompt 18B2-P6 §21 · §22 — three primary destinations. Tab
    // state is local: switching tabs never triggers auth flow.
    const [tab, setTab] = useState("home");
    // Prompt 18B2-P7 §1 · §2 — Staff Meal is REMOVED from Home
    // and captured inside the Clock Out flow instead. `staffMeal`
    // now holds the employee's in-flight Clock Out answer only.
    const [staffMeal, setStaffMeal] = useState(null);
    const [staffMealPending] = useState(false);
    // Prompt 18B2-P1 §12 — schedule-aware confirmation when the
    // employee taps Clock In and no scheduled shift remains today.
    const [confirmClockInOpen, setConfirmClockInOpen] = useState(false);
    // Clock In actual-start confirmation (published shift only).
    const [startFlow, setStartFlow] = useState(null);
    const [startPick, setStartPick] = useState("");
    // Prompt 18B2-P2 §8-§12 — schedule-aware Clock Out flow. State
    // holds either { mode: "yes-no", scheduledEnd } when exactly ONE
    // applicable Published Shift matches the active clock_in date,
    // or { mode: "pick", options: [...] } when no shift / ambiguous.
    const [clockOutFlow, setClockOutFlow] = useState(null);
    const [clockOutPick, setClockOutPick] = useState("");
    const idempotencyRef = useRef("");

    // Prompt 18B2-P8 §3 · §4 · §12 · §13 · §22 · §23 — Sick Leave
    // self-report flow (Sairasloma). One extra client tab
    // "sickleave" that renders the dedicated screen; More adds a
    // single canonical row that opens it. Persists to the
    // canonical `POST /workforce/employee/sick-leave` endpoint;
    // reads current/upcoming rows from the additive `sick_leaves`
    // list on the existing `GET /employee/schedule` payload.
    // Prompt 19B-BUILD-FIX — these Hooks previously lived below the
    // "Loading…" early-return, which violated the Rules of Hooks
    // and blocked `yarn build` with 6 `react-hooks/rules-of-hooks`
    // errors. Moved verbatim to the unconditional Hook section so
    // ESLint sees them on every render; behavior, initial values,
    // dependencies, API call, validation and UI copy are unchanged.
    const _todayLocalYMD = () => new Date().toISOString().slice(0, 10);
    const [sickStart, setSickStart] = useState(_todayLocalYMD());
    const [sickEnd, setSickEnd] = useState(_todayLocalYMD());
    const [sickErr, setSickErr] = useState(null);
    const [sickInfo, setSickInfo] = useState(null);
    const [sickPending, setSickPending] = useState(false);

    const _handleAuthErr = useCallback((status) => {
        if (status === 401) {
            onSessionExpired();
            return true;
        }
        return false;
    }, [onSessionExpired]);

    const _loadAll = useCallback(async () => {
        try {
            const [meRes, clockRes, schedRes] = await Promise.all([
                employeeApi.get("/workforce/employee/me"),
                employeeApi.get("/workforce/employee/clock"),
                employeeApi.get("/workforce/employee/schedule"),
            ]);
            setMe(meRes.data);
            setClock(clockRes.data);
            setSchedule(schedRes.data);
            setScheduleErr("");
        } catch (err) {
            if (_handleAuthErr(err?.response?.status)) return;
            setScheduleErr(i18n.t("wf.employeeWorkforceMobile.couldNotLoadYourScheduleTry", "Could not load your schedule. Try again."));
        }
    }, [_handleAuthErr]);

    useEffect(() => { _loadAll(); }, [_loadAll]);

    const _refreshClock = useCallback(async () => {
        try {
            const { data } = await employeeApi.get(
                "/workforce/employee/clock");
            setClock(data);
        } catch (err) {
            _handleAuthErr(err?.response?.status);
        }
    }, [_handleAuthErr]);

    const _doClockIn = useCallback(async (resolution, declared) => {
        // Prompt 18B2-P3 §4 · §5 · §6 · §8 — send an optional
        // `reported_work_start` derived from the canonical
        // 10-minute rounding rule (0-5 rounds DOWN, 6-9 rounds UP).
        // If we can unambiguously match the imminent scheduled shift
        // within a normal 10-minute tolerance, prefer the scheduled
        // start (Yes-near-shift shortcut · §6). Materially early or
        // late Clock In does NOT get the shortcut (§7 · §8).
        // CLOCK C2 — the server resolves the restaurant from the
        // applicable published shift; Mobile never picks one.
        const res = resolution || schedule?.clock_resolution || {};
        setPending(true); setClockErr("");
        idempotencyRef.current = `ci-${Date.now()}-${Math.random()
            .toString(36).slice(2)}`;
        const now = new Date();
        const hh = now.getHours();
        const mm = now.getMinutes();
        const rounded = mm % 10 <= 5
            ? Math.floor(mm / 10) * 10
            : (Math.floor(mm / 10) + 1) * 10;
        let rHH = hh, rMM = rounded;
        if (rMM === 60) { rHH = (hh + 1) % 24; rMM = 0; }
        let reported = `${String(rHH).padStart(2, "0")}:${
            String(rMM).padStart(2, "0")}`;
        // Prompt 18B2-P3 §6 · §7 — scheduled-start shortcut for the
        // resolved shift only.
        if (res.kind === "PUBLISHED_SHIFT" && res.start_time) {
            const [sHH, sMM] = res.start_time.split(":").map(Number);
            const diff = (hh * 60 + mm) - (sHH * 60 + sMM);
            if (diff >= -10 && diff <= 5) reported = res.start_time;
        }
        const canonical = {
            reported_work_start: reported,
        };
        try {
            await employeeApi.post("/workforce/employee/clock-in", {
                idempotency_key: idempotencyRef.current,
                ...(declared || canonical),
            });
            setStartFlow(null);
            await _refreshClock();
        } catch (err) {
            if (_handleAuthErr(err?.response?.status)) return;
            const code = err?.response?.data?.detail?.code;
            if (declared && code === "WORKFORCE_CLOCK_REPORTED_TIME_INVALID") {
                setStartFlow((prev) => prev && { ...prev, error:
                    i18n.t("wf.employeeWorkforceMobile.thatStartTimeCanTBe", "That start time can't be used. It can't be in the future or before your previous shift ended.") });
                return;
            }
            setStartFlow(null);
            setClockErr(_friendlyClockError(
                err?.response?.status, code));
        } finally { setPending(false); }
    }, [schedule, _refreshClock, _handleAuthErr]);

    const _clockIn = useCallback(async () => {
        if (pending || !me || !clock) return;
        // CLOCK C2 — fresh server resolution at the moment of the tap.
        let res = schedule?.clock_resolution || {};
        try {
            const { data } = await employeeApi.get("/workforce/employee/schedule");
            setSchedule(data);
            res = data?.clock_resolution || {};
        } catch (err) {
            if (_handleAuthErr(err?.response?.status)) return;
        }
        if (res.kind === "AMBIGUOUS") {
            setClockErr(i18n.t("wf.EmployeeWorkforceMobile._MSG_CLOCK_AMBIGUOUS", _MSG_CLOCK_AMBIGUOUS));
            return;
        }
        if (res.kind === "NO_MAIN_RESTAURANT" || !res.kind) {
            setClockErr(i18n.t("wf.employeeWorkforceMobile.noLocationAssignedContactYourManager", "No location assigned. Contact your manager."));
            return;
        }
        // Prompt 18B2-P1 §12 — no applicable shift: keep the explicit
        // confirmation (unscheduled entry, main restaurant only).
        if (res.kind === "UNSCHEDULED_MAIN_RESTAURANT") {
            setClockErr("");
            setConfirmClockInOpen(true);
            return;
        }
        if (scheduledStartConfirmable(res, new Date())) {
            setClockErr("");
            setStartFlow({ mode: "ask", res, startTime: res.start_time });
            return;
        }
        await _doClockIn(res);
    }, [pending, me, clock, schedule, _doClockIn, _handleAuthErr]);

    const _startDifferent = useCallback(() => {
        const prev = (clock?.recent_events || [])[0];
        const floorIso = prev && prev.event_type === "CLOCK_OUT"
            ? (prev.reported_occurred_at || prev.occurred_at) : null;
        const options = declaredStartOptions(new Date(), floorIso);
        setStartPick(options.length ? options[options.length - 1].value : "");
        setStartFlow((f) => f && { ...f, mode: "pick", options, error: "" });
    }, [clock]);

    const _startSubmit = useCallback(() => {
        const opt = (startFlow?.options || []).find((o) => o.value === startPick);
        if (!opt) return;
        _doClockIn(startFlow.res, { reported_work_start: opt.time, reported_work_start_date: opt.date });
    }, [startFlow, startPick, _doClockIn]);

    const _confirmedClockIn = useCallback(async () => {
        setConfirmClockInOpen(false);
        await _doClockIn(schedule?.clock_resolution);
    }, [_doClockIn, schedule]);

    const _clockOut = useCallback(async () => {
        if (pending || !me || !clock) return;
        // Prompt 18B2-P2 §8-§12 — open the schedule-aware Clock Out
        // flow. Decide UI mode from the immutable Published Snapshot
        // and the active CLOCK_IN date.
        const activeAt = clock?.active_event?.occurred_at || "";
        // TES-B2C1B1M2 — UTC evidence → device-local date/time.
        const inDate = localYMD(new Date(activeAt));
        const activeRid = clock?.active_event?.restaurant_id;
        const shiftsToday = [];
        for (const pub of (schedule?.publications || [])) {
            if (activeRid && pub.restaurant_id !== activeRid) continue;
            for (const s of (pub.shifts || [])) {
                if (s.work_date === inDate) shiftsToday.push(s);
            }
        }
        // Build 10-minute options inside the backend-valid window.
        const nowD = new Date();
        const { startMin, endMin } = clockOutBounds(activeAt, nowD);
        const options = [];
        for (let t = startMin; t <= endMin; t += 10) {
            options.push(fmtHM(new Date(t * 60000)));
        }
        if (shiftsToday.length === 1) {
            // TES-B2C1B1M3 — a future/unreachable end is never offered.
            const confirmable = scheduledEndConfirmable(
                shiftsToday[0], activeAt, nowD);
            setClockOutFlow({
                mode: confirmable ? "yes-no" : "pick",
                scheduledEnd: shiftsToday[0].end_time,
                options,
            });
            setClockOutPick(pickValidFinish(options, shiftsToday[0].end_time));
        } else {
            setClockOutFlow({ mode: "pick", options });
            setClockOutPick(pickValidFinish(options, ""));
        }
    }, [pending, me, clock, schedule]);

    const _doClockOut = useCallback(async (
        reportedWorkEnd, mealAnswer, confirmScheduledEnd) => {
        // Prompt 18B2-P7 §2 · §3 · §5 — Staff Meal is REQUIRED
        // before the final Clock Out POST. When callers pass no
        // meal answer yet, advance the flow to the meal step
        // instead of hitting the network so the two facts can be
        // persisted atomically in ONE round-trip.
        if (mealAnswer !== true && mealAnswer !== false) {
            setClockOutFlow((prev) => ({
                mode: "meal",
                reportedWorkEnd: reportedWorkEnd || null,
                confirmScheduledEnd: prev?.mode === "yes-no",
                scheduledEnd: prev?.scheduledEnd,
                options: prev?.options,
            }));
            return;
        }
        setPending(true); setClockErr("");
        idempotencyRef.current = `co-${Date.now()}-${Math.random()
            .toString(36).slice(2)}`;
        try {
            const body = {
                idempotency_key: idempotencyRef.current,
                staff_meal: mealAnswer,
            };
            if (reportedWorkEnd) body.reported_work_end = reportedWorkEnd;
            if (reportedWorkEnd && confirmScheduledEnd) {
                body.confirm_scheduled_end = true;
            }
            await employeeApi.post(
                "/workforce/employee/clock-out", body);
            await _refreshClock();
            setClockOutFlow(null);
            setClockOutPick("");
            setStaffMeal(null);
        } catch (err) {
            if (_handleAuthErr(err?.response?.status)) return;
            const code = err?.response?.data?.detail?.code;
            setClockErr(_friendlyClockError(
                err?.response?.status, code));
        } finally { setPending(false); }
    }, [_refreshClock, _handleAuthErr]);

    // Prompt 18B2-P8 §3 · §4 · §12 · §13 · §22 · §23 — Sick Leave
    // submit handler. Kept next to the other clock-related
    // callbacks (P6/P7) and unconditionally declared so the Rules
    // of Hooks stay satisfied even before the "Loading…" branch.
    const _submitSickLeave = useCallback(async () => {
        if (sickPending) return;
        setSickErr(null);
        setSickInfo(null);
        if (!sickStart || !sickEnd || sickEnd < sickStart) {
            setSickErr(i18n.t("wf.employeeWorkforceMobile.endDateMustBeOnOr", "End date must be on or after start date."));
            return;
        }
        setSickPending(true);
        try {
            const { data } = await employeeApi.post(
                "/workforce/employee/sick-leave",
                { start_date: sickStart, end_date: sickEnd });
            setSickInfo({
                start_date: data.start_date,
                end_date: data.end_date,
            });
            // Refresh schedule so the "Current sick leave" summary
            // hydrates immediately (§13 · §22.15).
            try {
                const { data: sched } = await employeeApi.get(
                    "/workforce/employee/schedule");
                setSchedule(sched);
            } catch (_e) { /* silent */ }
        } catch (err) {
            if (_handleAuthErr(err?.response?.status)) return;
            const code = err && err.response && err.response.data
                && err.response.data.detail
                && (typeof err.response.data.detail === "object"
                    ? err.response.data.detail.code
                    : err.response.data.detail);
            if (code === "WORKFORCE_ABSENCE_OVERLAP") {
                setSickErr(
                    i18n.t("wf.employeeWorkforceMobile.youAlreadyHaveAnAbsenceCovering", "You already have an absence covering these dates."));
            } else if (code === "WORKFORCE_PERIOD_CLOSED") {
                setSickErr(
                    i18n.t("wf.employeeWorkforceMobile.datesInClosedPeriod", "These dates are inside a closed period. Contact your manager."));
            } else {
                setSickErr(i18n.t("wf.employeeWorkforceMobile.couldNotReportSickLeaveTry", "Could not report sick leave. Try again."));
            }
        } finally {
            setSickPending(false);
        }
    }, [sickStart, sickEnd, sickPending, _handleAuthErr]);

    if (!me || !clock) {
        return (
            <div
                data-testid="employee-workforce-home-loading"
                className="text-sm text-neutral-500 text-center py-10"
            >
                {i18n.t("common.loading", "Loading…")}
            </div>
        );
    }

    // Prompt 18B2-P7 §1 · §2 — Staff Meal was moved from Home to
    // the Clock Out flow. The Home card is gone; `staffMeal` is
    // now the in-flight Clock Out answer only.
    void staffMealPending;

    const isClockedIn = clock.state === "IN";
    const activeAt = clock.active_event?.occurred_at;

    // Prompt 18B2-P6 §7 · §8 — friendly human-readable current
    // date. Uses the browser's local `en-GB`-style formatting so
    // the displayed weekday / month match the employee's device.
    // Prompt 18B2-P6A §2 · §4 — the LIVE analog+digital clock is
    // owned by `LiveMobileClock` (isolated 15s tick). The date
    // label is computed once at render because the day boundary
    // is not covered by this micro-fix.
    const _nowD = new Date();
    const dateLabel = localizeDateNames(_nowD.toLocaleDateString(undefined, {
        weekday: "long", day: "numeric", month: "long",
    }), { longMonth: true });
    // Prompt 18B2-P6 §10 · §11 — today's published shift + real
    // restaurant name (no raw IDs). Multiple shifts today render
    // as separate rows.
    const today = schedule?.today_local_date
        || `${_nowD.getFullYear()}-${
            String(_nowD.getMonth() + 1).padStart(2, "0")}-${
            String(_nowD.getDate()).padStart(2, "0")}`;
    const todaysPubs = [];
    for (const pub of (schedule?.publications || [])) {
        for (const s of (pub.shifts || [])) {
            if (s.work_date === today) {
                todaysPubs.push({
                    shift: s,
                    restaurant_name: pub.restaurant_name || "Restaurant",
                });
            }
        }
    }
    // CLOCK C2 — show the server-resolved Clock In restaurant first.
    const primaryRestaurant = schedule?.clock_resolution?.restaurant_name
        || todaysPubs[0]?.restaurant_name
        || "Restaurant";

    // Prompt 18B2-P6 §21 — "My shifts" render (Published-only,
    // ordered chronologically). Reuses the existing SchedulePanel.
    const _renderShifts = () => (
        <section
            data-testid="employee-workforce-shifts-tab"
            className="space-y-3 px-4"
        >
            <h2
                className="text-neutral-900 text-xl"
            >
                {i18n.t("wf.employeeWorkforceMobile.myShifts", "My shifts")}
            </h2>
            <p className="text-xs text-neutral-500">
                {i18n.t("wf.employeeWorkforceMobile.yourPublishedSchedule", "Your published schedule.")}
            </p>
            <SchedulePanel schedule={schedule} error={scheduleErr} />
        </section>
    );

    const _renderSickLeave = () => {
        const own = Array.isArray(schedule && schedule.sick_leaves)
            ? schedule.sick_leaves : [];
        return (
            <section
                data-testid="employee-workforce-sickleave-tab"
                className="space-y-4 px-1"
            >
                <button
                    type="button"
                    data-testid="employee-workforce-sickleave-back"
                    onClick={() => setTab("more")}
                    className="text-xs text-neutral-500 px-3"
                >
                    ← Back
                </button>
                <h2
                    className="text-neutral-900 text-2xl px-3"
                >
                    {i18n.t("wf.employeeWorkforceMobile.sickLeave", "Sick leave")}
                </h2>
                <p className="text-sm text-neutral-600 px-3">
                    {i18n.t("wf.employeeWorkforceMobile.reportTheDatesYouAreUnable", "Report the dates you are unable to work.")}
                </p>
                {own.length > 0 && (
                    <div
                        data-testid="employee-workforce-sickleave-current"
                        className="px-3"
                    >
                        <p className="text-[11px] text-neutral-500
                                        tracking-[0.14em] uppercase mb-1">
                            {i18n.t("wf.employeeWorkforceMobile.currentSickLeave", "Current sick leave")}
                        </p>
                        <ul className="divide-y divide-neutral-200/70">
                            {own.map((row) => (
                                <li
                                    key={row.absence_id}
                                    data-testid={
                                        `employee-workforce-sickleave-row-${row.absence_id}`}
                                    className="py-2 text-sm text-neutral-800"
                                >
                                    {row.start_date} – {row.end_date}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="pt-2 border-t border-neutral-200/60
                                divide-y divide-neutral-200/60">
                    <label className="flex items-center justify-between
                                        px-3 py-3">
                        <span className="text-sm text-neutral-700">
                            {i18n.t("wf.employeeWorkforceMobile.startDate", "Start date")}
                        </span>
                        <input
                            type="date"
                            data-testid="employee-workforce-sickleave-start"
                            value={sickStart}
                            onChange={(e) => setSickStart(e.target.value)}
                            className="bg-transparent text-neutral-900 text-sm
                                        text-right focus:outline-none"
                        />
                    </label>
                    <label className="flex items-center justify-between
                                        px-3 py-3">
                        <span className="text-sm text-neutral-700">
                            {i18n.t("wf.employeeWorkforceMobile.endDate", "End date")}
                        </span>
                        <input
                            type="date"
                            data-testid="employee-workforce-sickleave-end"
                            value={sickEnd}
                            onChange={(e) => setSickEnd(e.target.value)}
                            className="bg-transparent text-neutral-900 text-sm
                                        text-right focus:outline-none"
                        />
                    </label>
                </div>
                {sickErr && (
                    <p
                        data-testid="employee-workforce-sickleave-error"
                        className="text-sm text-red-600 px-3"
                    >
                        {sickErr}
                    </p>
                )}
                {sickInfo && (
                    <p
                        data-testid="employee-workforce-sickleave-success"
                        className="text-sm text-emerald-700 px-3"
                    >
                        {i18n.t("wf.employeeWorkforceMobile.sickLeaveReported", "Sick leave reported: {{start}} – {{end}}", { start: sickInfo.start_date, end: sickInfo.end_date })}
                    </p>
                )}
                <div className="px-3">
                    <Button
                        data-testid="employee-workforce-sickleave-submit"
                        onClick={_submitSickLeave}
                        disabled={sickPending}
                        className="min-h-[54px] w-full rounded-xl text-base
                                    font-medium bg-neutral-900
                                    hover:bg-neutral-800 text-white"
                    >
                        {sickPending ? i18n.t("wf.employeeWorkforceMobile.reporting", "Reporting…") : i18n.t("wf.employeeWorkforceMobile.reportSickLeave", "Report sick leave")}
                    </Button>
                    <p className="text-xs text-neutral-500 mt-3">
                        {i18n.t("wf.employeeWorkforceMobile.contactYourManagerIfTheDates", "Contact your manager if the dates need to be changed.")}
                    </p>
                </div>
            </section>
        );
    };

    // Prompt 18B2-P6 §22 · §24 · Prompt 18B2-P8 §3 · §23 ·
    // Prompt 18B2-P9 §21 · §41 — "More" is a restrained native
    // settings list. No identity card, no big filled buttons —
    // just row · label · chevron.
    const _renderMore = () => (
        <section
            data-testid="employee-workforce-more-tab"
            className="space-y-4 px-1"
        >
            <h2
                className="text-neutral-900 text-2xl px-3"
            >
                {i18n.t("wf.employeeWorkforceMobile.more", "More")}
            </h2>
            <p
                data-testid="employee-workforce-more-name"
                className="px-3 text-xs text-neutral-500"
            >
                {i18n.t("wf.employeeWorkforceMobile.signedInAs", "Signed in as {{name}}", { name: `${me.first_name} ${me.last_name}` })}
                {(me.worker_type || "DIRECT") === "EXTRA" && (
                    <span
                        data-testid="employee-workforce-more-extra-badge"
                        className="ml-2 inline-flex items-center
                                    rounded-full border
                                    border-neutral-300 bg-neutral-100
                                    px-2 py-0.5 text-[10px]
                                    font-medium uppercase
                                    tracking-wide text-neutral-700"
                    >
                        {i18n.t("wf.employeeWorkforceMobile.extra", "Extra")}
                    </span>
                )}
            </p>
            <ul className="divide-y divide-neutral-200/70">
                <li>
                    <button
                        type="button"
                        data-testid="employee-workforce-more-sickleave"
                        onClick={() => {
                            setSickErr(null);
                            setSickInfo(null);
                            setSickStart(_todayLocalYMD());
                            setSickEnd(_todayLocalYMD());
                            setTab("sickleave");
                        }}
                        className="w-full px-3 py-3 text-left flex items-center
                                    justify-between active:bg-neutral-100/40"
                    >
                        <span>
                            <span className="block text-base text-neutral-900">
                                {i18n.t("wf.employeeWorkforceMobile.sickLeave", "Sick leave")}
                            </span>
                            <span className="block text-xs text-neutral-500 mt-0.5">
                                {i18n.t("wf.employeeWorkforceMobile.reportIllnessDates", "Report illness dates")}
                            </span>
                        </span>
                        <span
                            aria-hidden="true"
                            className="text-neutral-400"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24"
                                 fill="none" stroke="currentColor"
                                 strokeWidth="1.6" strokeLinecap="round"
                                 strokeLinejoin="round">
                                <path d="M9 6l6 6-6 6" />
                            </svg>
                        </span>
                    </button>
                </li>
                <li>
                    <button
                        type="button"
                        data-testid="employee-workforce-logout"
                        onClick={onLogout}
                        className="w-full px-3 py-3 text-left flex items-center
                                    justify-between active:bg-neutral-100/40"
                    >
                        <span className="text-base text-neutral-900">
                            {i18n.t("wf.employeeWorkforceMobile.logOut", "Log out")}
                        </span>
                        <span
                            aria-hidden="true"
                            className="text-neutral-400"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24"
                                 fill="none" stroke="currentColor"
                                 strokeWidth="1.6" strokeLinecap="round"
                                 strokeLinejoin="round">
                                <path d="M9 6l6 6-6 6" />
                            </svg>
                        </span>
                    </button>
                </li>
            </ul>
            <button
                type="button"
                data-testid="employee-workforce-forget-device"
                onClick={onForgetDevice}
                className="text-xs text-neutral-500 underline text-center
                            w-full mt-2"
            >
                {i18n.t("wf.employeeWorkforceMobile.activateAnotherDeviceAccount", "Activate another device / account")}
            </button>
            <div className="px-3 pt-2 flex items-center justify-between gap-3">
                <span className="text-xs text-neutral-500">Language / Kieli</span>
                <MobileLanguageSelector />
            </div>
        </section>
    );

    // Prompt 18B2-P6 §3-§20 · Prompt 18B2-P9 §3-§17 · §40 — Home:
    // MiseOne Workforce brand, refined watch-face clock, live
    // digital time, greeting, date, subtle status dot, native
    // "TODAY" section with spacing-only rows (no heavy card,
    // §11 · §33) and one dominant primary Clock CTA (§15).
    const _renderHome = () => (
        <div
            data-testid="employee-workforce-home"
            className="space-y-4 pb-2"
        >
            {/* Prompt 18B2-P9 §5 — compact brand header. */}
            <header className="pt-1 pb-0 text-center">
                <h1
                    data-testid="mobile-brand-miseone"
                    className="text-neutral-900 text-2xl"
                    style={{
                        fontFamily: "Georgia, 'Times New Roman', serif",
                    }}
                >
                    {i18n.t("wf.employeeWorkforceMobile.miseone", "MiseOne")}
                </h1>
                <p
                    data-testid="mobile-brand-workforce"
                    className="text-neutral-500 text-[11px] mt-0.5
                                tracking-[0.18em] uppercase"
                >
                    {i18n.t("wf.employeeWorkforceMobile.workforce", "Workforce")}
                </p>
            </header>
            {/* TES-B2C1B1M — greeting, then digital time only (no
                analog face), then date. `centerLabel=""` kept as the
                P9 contract; the digital clock ignores it. */}
            <div className="pt-5 pb-1 text-center">
                <p
                    data-testid="mobile-greeting"
                    className="text-neutral-900 text-xl font-medium"
                >
                    {me.first_name
                        ? i18n.t("wf.employeeWorkforceMobile.hiName", "Hi, {{name}}", { name: me.first_name })
                        : i18n.t("wf.employeeWorkforceMobile.hiThere", "Hi, there")}
                </p>
                <div className="mt-5">
                    <LiveMobileClock centerLabel="" />
                </div>
                <p
                    data-testid="mobile-date"
                    className="text-neutral-500 text-[15px] mt-2"
                >
                    {dateLabel}
                </p>
            </div>
            {/* Prompt 18B2-P9 §10 — clock status as a subtle inline
                dot + text. Never a full-width grey capsule. */}
            <p
                data-testid="employee-workforce-clock-status"
                className="text-center text-sm text-neutral-700"
            >
                <span
                    aria-hidden="true"
                    className={`inline-block h-2 w-2 rounded-full mr-2
                        align-middle ${
                        isClockedIn
                            ? "bg-emerald-500"
                            : "bg-neutral-300"}`}
                />
                {isClockedIn ? (
                    <>{i18n.t("wf.employeeWorkforceMobile.clockedIn", "Clocked in")}{activeAt
                        ? ` · ${_humanClockTime(activeAt)}` : ""}</>
                ) : i18n.t("wf.employeeWorkforceMobile.notClockedIn", "Not clocked in")}
            </p>
            {/* Prompt 18B2-P9 §11 · §12 · §13 · §33 — TODAY section
                as a spacing-only list with a subtle top divider. Never wrap in a bordered card. Restaurant row is
                suppressed when no meaningful value is present. */}
            <section
                data-testid="mobile-today-section"
                className="rounded-2xl bg-white px-4 py-3.5
                            border border-neutral-200/70
                            shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
            >
                <p className="text-[11px] text-neutral-500
                                tracking-[0.14em] uppercase mb-1">
                    {i18n.t("wf.employeeWorkforceMobile.today", "Today")}
                </p>
                <p
                    data-testid="mobile-today-shift"
                    className="text-neutral-900 text-[17px] font-medium
                                tabular-nums"
                >
                    {todaysPubs.length === 0
                        ? i18n.t("wf.employeeWorkforceMobile.noPublishedShift", "No published shift")
                        : todaysPubs
                            .map((r) => `${r.shift.start_time} – ${
                                r.shift.end_time}`)
                            .join(" · ")}
                </p>
                {todaysPubs.length > 0
                    && primaryRestaurant
                    && primaryRestaurant !== "Restaurant" && (
                    <p
                        data-testid="mobile-restaurant"
                        className="text-neutral-500 text-sm mt-0.5"
                    >
                        {primaryRestaurant}
                    </p>
                )}
            </section>
            {/* Prompt 18B2-P9 §15 · §16 · §34 — one dominant primary
                Clock CTA. Moderate rounded corners; native-height
                touch target (~54px). */}
            <div className="pt-1">
                {isClockedIn ? (
                    <Button
                        data-testid="employee-workforce-clock-out"
                        onClick={_clockOut}
                        disabled={pending}
                        className="w-full min-h-[54px] rounded-xl text-base
                                    font-medium bg-neutral-900
                                    hover:bg-neutral-800 text-white
                                    transition-opacity active:opacity-90"
                    >
                        {i18n.t("wf.employeeWorkforceMobile.clockOut", "Clock out")}
                    </Button>
                ) : (
                    <Button
                        data-testid="employee-workforce-clock-in"
                        onClick={_clockIn}
                        disabled={pending}
                        className="w-full min-h-[54px] rounded-xl text-base
                                    font-medium bg-neutral-900
                                    hover:bg-neutral-800 text-white
                                    transition-opacity active:opacity-90"
                    >
                        {i18n.t("wf.employeeWorkforceMobile.clockIn", "Clock in")}
                    </Button>
                )}
                {isClockedIn && _isStaleSession(activeAt) && (
                    <p data-testid="employee-workforce-stale-session"
                        className="text-sm text-amber-700 mt-2 text-center">
                        {i18n.t("wf.EmployeeWorkforceMobile._MSG_CLOCK_STALE_SESSION", _MSG_CLOCK_STALE_SESSION)}
                    </p>
                )}
                {clockErr && (
                    <p
                        data-testid="employee-workforce-clock-error"
                        className="text-sm text-red-600 mt-2 text-center"
                    >
                        {clockErr}
                    </p>
                )}
            </div>
            {/* Preserved schedule surface (Home also carries the
                published schedule inline for tests). Visually
                hidden — the heavy list lives in the shifts tab. */}
            <section
                data-testid="employee-workforce-schedule"
                className="sr-only"
            >
                <SchedulePanel schedule={schedule} error={scheduleErr} />
            </section>
        </div>
    );

    return (
        <>
            <div
                className="px-4 pt-2 pb-24 min-h-screen bg-[#fbfaf7]"
                style={{
                    fontFamily: "'Satoshi', system-ui, -apple-system, sans-serif",
                }}
            >
                {tab === "home" && _renderHome()}
                {tab === "shifts" && _renderShifts()}
                {tab === "more" && _renderMore()}
                {tab === "sickleave" && _renderSickLeave()}
            </div>
            <EmployeeBottomNav current={tab} onChange={setTab} />
            {/* Prompt 18B2-P1 §12 — schedule-aware Clock In
                confirmation. Simple inline dialog (no external
                dependency) so the Employee Mobile bundle stays
                self-contained. Cancel = no clock event. Confirm =
                legitimate second Clock session (§14). */}
            {confirmClockInOpen && (
                <div
                    data-testid="employee-workforce-clock-in-confirm"
                    role="dialog"
                    aria-modal="true"
                    className="fixed inset-0 z-50 flex items-end sm:items-center
                                justify-center bg-black/40 p-4"
                >
                    <div
                        className="w-full max-w-sm rounded-t-2xl sm:rounded-2xl
                                    bg-white p-4 space-y-3 shadow-xl"
                    >
                        <h3
                            data-testid="employee-workforce-clock-in-confirm-title"
                            className="text-base font-medium text-neutral-900"
                        >
                            {i18n.t("wf.employeeWorkforceMobile.noMoreScheduledShiftsToday", "No more scheduled shifts today")}
                        </h3>
                        <p
                            data-testid="employee-workforce-clock-in-confirm-body"
                            className="text-sm text-neutral-600"
                        >
                            {i18n.t("wf.employeeWorkforceMobile.youHaveNoMoreScheduledShifts", "You have no more scheduled shifts today. Clock in anyway?")}
                        </p>
                        <div className="flex gap-2 pt-1">
                            <Button
                                variant="outline"
                                data-testid="employee-workforce-clock-in-confirm-cancel"
                                onClick={() => setConfirmClockInOpen(false)}
                                className="flex-1 min-h-[44px]"
                            >
                                {i18n.t("common.cancel", "Cancel")}
                            </Button>
                            <Button
                                data-testid="employee-workforce-clock-in-confirm-ok"
                                onClick={_confirmedClockIn}
                                disabled={pending}
                                className="flex-1 min-h-[44px]"
                            >
                                {i18n.t("wf.employeeWorkforceMobile.clockInAnyway2", "Clock in anyway")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {startFlow && (
                <ClockInStartDialog
                    flow={startFlow}
                    pick={startPick}
                    onPick={setStartPick}
                    pending={pending}
                    onYes={() => _doClockIn(startFlow.res, {
                        reported_work_start: startFlow.startTime, confirm_scheduled_start: true })}
                    onDifferent={_startDifferent}
                    onSubmit={_startSubmit}
                    onCancel={() => { setStartFlow(null); setStartPick(""); }}
                />
            )}

            {/* Prompt 18B2-P2 §8-§12 — schedule-aware Clock Out
                flow. Yes/No when exactly one applicable Published
                Shift matches; manual 10-minute picker otherwise. */}
            {clockOutFlow && (
                <div
                    data-testid="employee-workforce-clock-out-flow"
                    role="dialog"
                    aria-modal="true"
                    className="fixed inset-0 z-50 flex items-end sm:items-center
                                justify-center bg-black/40 p-4"
                >
                    <div
                        className="w-full max-w-sm rounded-t-2xl sm:rounded-2xl
                                    bg-white p-4 space-y-3 shadow-xl"
                    >
                        {clockOutFlow.mode === "yes-no" ? (
                            <>
                                <h3
                                    data-testid="employee-workforce-clock-out-flow-title"
                                    className="text-base font-medium text-neutral-900"
                                >
                                    {i18n.t("wf.employeeWorkforceMobile.didYourShiftEndAtThe", "Did your shift end at the scheduled time?")}
                                </h3>
                                <p className="text-sm text-neutral-600">
                                    {i18n.t("wf.employeeWorkforceMobile.scheduledEnd", "Scheduled end:")}{" "}
                                    <span data-testid=
                                        "employee-workforce-clock-out-scheduled-end">
                                        {clockOutFlow.scheduledEnd}
                                    </span>
                                </p>
                                <div className="flex flex-col gap-2 pt-1">
                                    <Button
                                        data-testid="employee-workforce-clock-out-yes"
                                        onClick={() => _doClockOut(
                                            clockOutFlow.scheduledEnd)}
                                        disabled={pending}
                                        className="min-h-[44px]"
                                    >
                                        {i18n.t("wf.employeeWorkforceMobile.yesEndedAt", "Yes, ended at {{time}}", { time: clockOutFlow.scheduledEnd })}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        data-testid="employee-workforce-clock-out-no"
                                        onClick={() => setClockOutFlow({
                                            ...clockOutFlow, mode: "pick" })}
                                        disabled={pending}
                                        className="min-h-[44px]"
                                    >
                                        {i18n.t("wf.employeeWorkforceMobile.noChooseFinishTime", "No, choose finish time")}
                                    </Button>
                                </div>
                            </>
                        ) : clockOutFlow.mode === "pick" ? (
                            <>
                                <h3
                                    data-testid="employee-workforce-clock-out-flow-title"
                                    className="text-base font-medium text-neutral-900"
                                >
                                    {i18n.t("wf.employeeWorkforceMobile.whatTimeDidYouFinishWorking", "What time did you finish working?")}
                                </h3>
                                <select
                                    data-testid="employee-workforce-clock-out-picker"
                                    className="w-full rounded-md border
                                                border-neutral-200 bg-white
                                                px-3 py-2 text-sm min-h-[44px]"
                                    value={clockOutPick}
                                    onChange={(e) => setClockOutPick(e.target.value)}
                                >
                                    {(clockOutFlow.options || []).map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                                <div className="flex gap-2 pt-1">
                                    <Button
                                        variant="outline"
                                        data-testid="employee-workforce-clock-out-cancel"
                                        onClick={() => {
                                            setClockOutFlow(null);
                                            setClockOutPick("");
                                            setStaffMeal(null);
                                        }}
                                        disabled={pending}
                                        className="flex-1 min-h-[44px]"
                                    >
                                        {i18n.t("common.cancel", "Cancel")}
                                    </Button>
                                    <Button
                                        data-testid="employee-workforce-clock-out-submit"
                                        onClick={() => _doClockOut(clockOutPick)}
                                        disabled={pending || !(clockOutFlow.options
                                            || []).includes(clockOutPick)}
                                        className="flex-1 min-h-[44px]"
                                    >
                                        {i18n.t("common.next", "Next")}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Prompt 18B2-P7 §2 · §3 · §9 —
                                    Staff Meal is REQUIRED at Clock
                                    Out. Two large Yes / No buttons with clear pressed-state so the
                                    employee sees the selection. */}
                                <h3
                                    data-testid="employee-workforce-clock-out-meal-title"
                                    className="text-base font-medium text-neutral-900"
                                >
                                    {i18n.t("wf.employeeWorkforceMobile.didYouHaveAStaffMeal", "Did you have a staff meal today?")}
                                </h3>
                                <p className="text-xs text-neutral-500">
                                    {i18n.t("wf.employeeWorkforceMobile.answerRequiredThisIsA", "Answer required — this is a payroll-relevant fact.")}
                                </p>
                                <div className="grid grid-cols-2 gap-2 pt-1">
                                    <Button
                                        data-testid="employee-workforce-clock-out-meal-yes"
                                        aria-pressed={staffMeal === true}
                                        variant={staffMeal === true
                                            ? "default" : "outline"}
                                        onClick={() => setStaffMeal(true)}
                                        disabled={pending}
                                        className={`min-h-[52px] ${
                                            staffMeal === true
                                                ? "bg-neutral-900 text-white"
                                                : ""}`}
                                    >
                                        {i18n.t("common.yes", "Yes")}
                                    </Button>
                                    <Button
                                        data-testid="employee-workforce-clock-out-meal-no"
                                        aria-pressed={staffMeal === false}
                                        variant={staffMeal === false
                                            ? "default" : "outline"}
                                        onClick={() => setStaffMeal(false)}
                                        disabled={pending}
                                        className={`min-h-[52px] ${
                                            staffMeal === false
                                                ? "bg-neutral-900 text-white"
                                                : ""}`}
                                    >
                                        {i18n.t("common.no", "No")}
                                    </Button>
                                </div>
                                <div className="flex gap-2 pt-2">
                                    <Button
                                        variant="outline"
                                        data-testid="employee-workforce-clock-out-meal-cancel"
                                        onClick={() => {
                                            setClockOutFlow(null);
                                            setClockOutPick("");
                                            setStaffMeal(null);
                                        }}
                                        disabled={pending}
                                        className="flex-1 min-h-[44px]"
                                    >
                                        {i18n.t("common.cancel", "Cancel")}
                                    </Button>
                                    <Button
                                        data-testid="employee-workforce-clock-out-meal-submit"
                                        onClick={() => _doClockOut(
                                            clockOutFlow.reportedWorkEnd,
                                            staffMeal,
                                            clockOutFlow.confirmScheduledEnd)}
                                        disabled={pending
                                            || (staffMeal !== true
                                                && staffMeal !== false)}
                                        className="flex-1 min-h-[44px]"
                                    >
                                        {i18n.t("wf.employeeWorkforceMobile.clockOut", "Clock out")}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}


function _shiftDurationMinutes(entry) {
    // Mirrors backend `_shift_duration_minutes` for local display only.
    const wd = entry.work_date;
    const ed = entry.end_date;
    const [wy, wm, wd2] = wd.split("-").map(Number);
    const [ey, em, ed2] = ed.split("-").map(Number);
    const spanDays = Math.round(
        (Date.UTC(ey, em - 1, ed2) - Date.UTC(wy, wm - 1, wd2))
        / 86400000);
    const [sh, sm] = entry.start_time.split(":").map(Number);
    const [eh, em2] = entry.end_time.split(":").map(Number);
    return eh * 60 + em2 + spanDays * 24 * 60 - (sh * 60 + sm);
}


function SchedulePanel({ schedule, error }) {
    if (error) {
        return (
            <p
                data-testid="employee-workforce-schedule-error"
                className="text-sm text-red-600"
            >
                {error}
            </p>
        );
    }
    const publications = schedule?.publications || [];
    // Combine all shifts + day entries into a chronological list.
    const rows = [];
    for (const pub of publications) {
        for (const s of pub.shifts || []) {
            rows.push({
                key: `s-${s.schedule_entry_id}`,
                date: s.work_date,
                kind: "shift",
                start_time: s.start_time,
                end_date: s.end_date,
                end_time: s.end_time,
                duration_minutes: _shiftDurationMinutes(s),
            });
        }
        for (const d of pub.day_entries || []) {
            rows.push({
                key: `d-${d.day_entry_id}`,
                date: d.date,
                kind: "day",
                code: d.code,
            });
        }
    }
    if (rows.length === 0) {
        return (
            <p
                data-testid="employee-workforce-schedule-empty"
                className="text-sm text-neutral-500"
            >
                {i18n.t("wf.employeeWorkforceMobile.noPublishedScheduleYet", "No published schedule yet.")}
            </p>
        );
    }
    rows.sort((a, b) => (a.date < b.date ? -1
        : a.date > b.date ? 1
        : (a.start_time || "").localeCompare(b.start_time || "")));
    return (
        <ul
            data-testid="employee-workforce-schedule-list"
            className="space-y-1"
        >
            {rows.map((r) => {
                const hdr = formatScheduleDateHeader(r.date);
                const label = hdr
                    ? `${hdr.weekday} ${hdr.day} ${hdr.month}`
                    : r.date;
                return (
                    <li
                        key={r.key}
                        data-testid={`employee-workforce-schedule-item-${r.key}`}
                        className="flex justify-between items-baseline
                                    text-sm rounded-md border
                                    border-neutral-200 bg-white
                                    px-3 py-2"
                    >
                        <span className="text-neutral-700">{label}</span>
                        <span className="text-neutral-900 font-medium">
                            {r.kind === "shift"
                                ? (
                                    <>
                                        {r.start_time}–{r.end_time}
                                        {r.end_date !== r.date
                                            ? ` +${Math.max(1,
                                                Math.round(
                                                (new Date(r.end_date)
                                                    - new Date(r.date))
                                                / 86400000))} day`
                                            : ""}
                                        <span className="text-neutral-500
                                                        ml-2">
                                            {formatDurationMinutes(
                                                r.duration_minutes)}
                                        </span>
                                    </>
                                )
                                : (
                                    <span
                                        data-testid={`employee-workforce-schedule-code-${r.key}`}
                                    >
                                        {r.code}
                                    </span>
                                )}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}
